/**
 * R1 and R2's header (Scores UX spec v1.0 section 8): no card, a 56px round
 * mark, 16, then the team name as the page's `h1` on up to two lines (never
 * truncated) over "9-4 · 2nd AFC West" in `caption` `textSecondary`, and the
 * follow star at the right end of that row (ISSUE-1082, owner 2026-10-08),
 * with the always visible Alerts row beneath. The star never shrinks, so a
 * long name wraps instead of pushing it off.
 *
 * The header is not sticky and does not collapse. `GameDetailHeader`
 * implements a collapsing pattern, but D2 requires the next game visible
 * without scrolling at 390px, so a collapse threshold is unreachable in the
 * state D2 grades, and a scroll listener that never fires is dead code.
 *
 * No team color anywhere: `primaryColorHex` and `secondaryColorHex` are on
 * the wire and are deliberately not read here.
 */
import type { ReactNode } from "react";

import { TeamDisc } from "../scores/TeamDisc";
import { TeamFollowControl } from "./TeamFollowControl";
import type {
  typeAlertSwitchRow,
  typeTeamFollowViewState,
} from "./teamFollowLogic";
import { TeamFollowStarButton } from "./TeamFollowStarButton";
import { teamMetaLine } from "./teamProfileLogic";

export type typeTeamHeaderProps = {
  name: string;
  abbreviation: string;
  /** The team's logo, drawn in place of the plate when present (ISSUE-1087). */
  logoUrl?: string | null;
  recordLabel: string | null;
  standingLabel: string | null;
  viewState: typeTeamFollowViewState;
  isFollowing: boolean;
  alertRows: readonly typeAlertSwitchRow[];
  onToggleFollow: () => void;
  onOpenAlerts: () => void;
};

export const TeamHeader = (props: typeTeamHeaderProps): ReactNode => (
  <header
    data-testid="team-profile-header"
    className="flex flex-col gap-3 pt-2 pb-4"
  >
    <div
      className="flex items-center gap-4"
      data-testid="team-profile-header-row"
    >
      <TeamDisc
        plateText={props.abbreviation}
        logoUrl={props.logoUrl ?? null}
        size="page"
        testId="team-mark"
        imageTestId="team-mark-image"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h1
          data-testid="team-profile-name"
          className="text-h1 wrap-break-word text-ink"
        >
          {props.name}
        </h1>
        <p
          data-testid="team-profile-meta"
          className="text-caption text-ink-secondary tabular-nums"
        >
          {teamMetaLine({
            recordLabel: props.recordLabel,
            standingLabel: props.standingLabel,
          })}
        </p>
      </div>
      <TeamFollowStarButton
        testId="team-header-follow-star"
        teamName={props.name}
        isFollowing={props.isFollowing}
        isPending={props.viewState === "pending"}
        onToggle={props.onToggleFollow}
      />
    </div>
    <TeamFollowControl
      viewState={props.viewState}
      isFollowing={props.isFollowing}
      alertRows={props.alertRows}
      onOpenAlerts={props.onOpenAlerts}
    />
  </header>
);
